import React, { useEffect, useState } from "react";
import {
  pendingTransactions,
  settleTransaction,
} from "../services/transactionService";
import { useSearchParams } from "react-router-dom";
import { getAmountInCurrencyFormat, limitNumber } from "../utils/number";
import { Loader } from "../components/common/Loader";
import { toggleSidebar } from "../utils/sidebarToggle";
import {
  confirmAction,
  successAlert,
  errorAlert,
} from "../components/common/Swal";

function PendingTransactions() {
  const [data, setData] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFilter = searchParams.get("filter") || "all";
  const [filter, setFilter] = useState(urlFilter);
  const [rescheduleId, setRescheduleId] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 🔥 Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await pendingTransactions(filter);
      setData(res);
    } catch (err) {
      setError(err.message || "Error while fetching the data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSearchParams({ filter });
    fetchData();
  }, [filter, page, limit]);

  // 🔥 Stats
  const stats = data.reduce(
    (acc, item) => {
      if (item.type === "gold") {
        acc.goldWeight += item.weight || 0;
        acc.goldAmount += item.amount || 0;
      } else {
        acc.silverWeight += item.weight || 0;
        acc.silverAmount += item.amount || 0;
      }
      return acc;
    },
    { goldWeight: 0, goldAmount: 0, silverWeight: 0, silverAmount: 0 },
  );

  //   const handleAction = async (id, action, extra = {}) => {
  //     let message = "";

  //     if (action === "complete") message = "Mark this transaction as COMPLETE?";
  //     if (action === "reject")
  //       message = "Are you sure to REJECT this transaction?";
  //     if (action === "reschedule") message = "Confirm reschedule?";

  //     const confirm = window.confirm(message);
  //     if (!confirm) return;

  //     try {
  //       await axios.patch(`/api/transactions/${id}/${action}`, extra);
  //       setRescheduleId(null);
  //       setNewDate("");
  //       fetchData();
  //     } catch (err) {
  //       console.log(err);
  //     }
  //   };

  const handleAction = async (id, endpoint, payload) => {
    let message = "";
    let successMsg = "";

    if (payload.action === "complete") {
      message = "Mark this transaction as COMPLETE?";
      successMsg = "Transaction marked as completed ✅";
    }

    if (payload.action === "reject") {
      message = "Are you sure you want to REJECT this transaction?";
      successMsg = "Transaction rejected ❌";
    }

    if (payload.action === "reschedule") {
      message = "Confirm reschedule?";
      successMsg = "Transaction rescheduled 🔄";
    }

    // 🔥 SweetAlert Confirm
    const confirmed = await confirmAction(message);
    if (!confirmed) return;

    try {
      await settleTransaction(id, payload);

      setRescheduleId(null);
      setNewDate("");
      fetchData();

      // 🔥 Success Alert
      successAlert(successMsg);
    } catch (err) {
      console.error(err);

      // 🔥 Error Alert
      errorAlert("Action failed. Please try again.");
    }
  };
  const startIndex = (page - 1) * limit;
  const paginatedData = data.slice(startIndex, startIndex + limit);
  const totalPages = Math.max(1, Math.ceil(data.length / limit));

  return (
    <>
      <PageHeader />
      <div className="container mt-2">
        {/* 🔥 HEADER */}
        <div className="pending-header d-flex justify-content-between align-items-center mb-3">
          <select
            className="form-select w-auto shadow-sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="today">Today</option>
            <option value="tomorrow">Tomorrow</option>
            <option value="week">This Week</option>
            <option value="overdue">Overdue</option>
          </select>
          <div className="d-flex gap-2">
            <span className="badge rounded-pill bg-warning text-dark px-3 py-2 shadow-sm">
              ⚖️ Total Weight:{" "}
              {limitNumber(stats.goldWeight + stats.silverWeight)}
            </span>

            <span className="badge rounded-pill bg-info text-dark px-3 py-2 shadow-sm">
              💰 Total Amount:{" "}
              {getAmountInCurrencyFormat(stats.goldAmount + stats.silverAmount)}
            </span>
          </div>
        </div>

        {/* 🔥 STATS */}
        <div className="row g-3 mb-4">
          <div className="col-md-6 col-lg-3">
            <div
              className="card text-white shadow border-0"
              style={{ background: "linear-gradient(135deg,#d4853a,#b8751a)" }}
            >
              <div className="card-body text-center">
                <h6>Gold Weight</h6>
                <h4>{limitNumber(stats.goldWeight)}</h4>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-lg-3">
            <div
              className="card text-white shadow border-0"
              style={{ background: "linear-gradient(135deg,#28a745,#218838)" }}
            >
              <div className="card-body text-center">
                <h6>Gold Amount</h6>
                <h4>{getAmountInCurrencyFormat(stats.goldAmount)}</h4>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-lg-3">
            <div
              className="card text-white shadow border-0"
              style={{ background: "linear-gradient(135deg,#6c757d,#495057)" }}
            >
              <div className="card-body text-center">
                <h6>Silver Weight</h6>
                <h4>{limitNumber(stats.silverWeight)}</h4>
              </div>
            </div>
          </div>

          <div className="col-md-6 col-lg-3">
            <div
              className="card text-white shadow border-0"
              style={{ background: "linear-gradient(135deg,#17a2b8,#117a8b)" }}
            >
              <div className="card-body text-center">
                <h6>Silver Amount</h6>
                <h4>{getAmountInCurrencyFormat(stats.silverAmount)}</h4>
              </div>
            </div>
          </div>
        </div>

        {/* 🔥 TABLE */}
        <div className="table-responsive shadow-sm rounded">
          <table className="pending-table">
            <thead className="table-dark">
              <tr>
                <th>Serial</th>
                <th>Customer</th>
                <th>Type</th>
                <th>Section</th>
                <th>Weight</th>
                <th>Amount</th>
                <th>Post Date</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8}>
                    <div className="d-flex justify-content-center align-items-center">
                      <Loader height="20px" />
                    </div>
                  </td>
                </tr>
              ) : paginatedData?.length > 0 ? (
                paginatedData?.map((item) => {
                  const todayDate = new Date();
                  const isOverdue =
                    !compareDatesOnly(item.postDate, todayDate) &&
                    new Date(item.postDate) < todayDate;

                  return (
                    <tr
                      key={item.transactionId}
                      className={isOverdue ? "overdue" : ""}
                    >
                      <td className="fw-bold">{item.serialNo}</td>
                      <td>{item.customer}</td>
                      <td className="text-uppercase fw-semibold">
                        {item.type}
                      </td>
                      <td>{item.section}</td>
                      <td>{item.weight}</td>
                      <td>{getAmountInCurrencyFormat(item.amount)}</td>
                      <td>{new Date(item.postDate).toLocaleDateString()}</td>

                      <td>
                        {rescheduleId === item.transactionId ? (
                          <div className="d-flex gap-2 justify-content-center align-items-center">
                            <input
                              type="date"
                              className="form-control form-control-sm"
                              value={newDate}
                              onChange={(e) => setNewDate(e.target.value)}
                            />

                            <button
                              className="btn btn-success btn-sm"
                              onClick={() =>
                                handleAction(item.transactionId, "settle", {
                                  action: "reschedule",
                                  date: newDate,
                                  section: item.section,
                                  type: item.type,
                                })
                              }
                            >
                              ✔
                            </button>

                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => {
                                setRescheduleId(null);
                                setNewDate("");
                              }}
                            >
                              ✖
                            </button>
                          </div>
                        ) : (
                          <div className="d-flex gap-2 justify-content-center">
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() =>
                                // handleAction(item.transactionId, "complete", item)
                                handleAction(item.transactionId, "complete", {
                                  action: "complete",
                                  section: item.section,
                                  type: item.type,
                                })
                              }
                            >
                              ✔
                            </button>

                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() =>
                                // handleAction(item.transactionId, "reject", item)
                                handleAction(item.transactionId, "settle", {
                                  action: "reject",
                                  section: item.section,
                                  type: item.type,
                                })
                              }
                            >
                              ✖
                            </button>

                            <button
                              className="btn btn-warning btn-sm"
                              onClick={() =>
                                setRescheduleId(item.transactionId)
                              }
                            >
                              ⟳
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8}>
                    <div className="d-flex justify-content-center align-items-center">
                      No transaction found.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 🔥 PAGINATION */}
        <div className="d-flex justify-content-between align-items-center mt-3 mb-5">
          <select
            className="form-select w-auto"
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>

          <div className="d-flex gap-2 align-items-center">
            <button
              className="btn btn-outline-dark btn-sm"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              ←
            </button>

            <span className="fw-bold">
              {page} / {totalPages}
            </span>

            <button
              className="btn btn-outline-dark btn-sm"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              →
            </button>
          </div>
        </div>
        {/* <div className="d-flex justify-content-between align-items-center mt-3 mb-5">
        <div>
          <select
            className="form-select w-auto"
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
        </div>

        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Prev
          </button>

          <span className="align-self-center">Page {page}</span>

          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      </div> */}
      </div>
    </>
  );
}

function PageHeader() {
  return (
    <div className="mainHead d-flex align-items-center justify-content-between pt-3 py-3">
      <div className="headText ms-3">Pending Rate Work</div>
      <div className="headIcons d-flex me-3">
        <div className="iconMain">
          <i className="fa-solid fa-search" />
        </div>
        <a href="dashboard.html" className="iconMain ms-3 me-3">
          <i className="fa-solid fa-exclamation" style={{ color: "black" }} />
        </a>
        <div className="iconMain" id="sidebarToggle" onClick={toggleSidebar}>
          <i className="fa-solid fa-bars" />
        </div>
      </div>
    </div>
  );
}

function compareDatesOnly(date1, date2) {
  // Set both dates to the same time (midnight) to ignore the time portion
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  // Set time to midnight for both dates
  d1.setHours(0, 0, 0, 0);
  d2.setHours(0, 0, 0, 0);

  console.log({ d1, d2 });

  // Compare the dates
  return d1.getTime() === d2.getTime();
}

export default PendingTransactions;
